import { Alert, Box } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import {
  CargoType,
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
  OrderResponseDto,
} from '@planning/app/api'
import { InitializationWrapper } from '@planning/components'
import { railVisitService } from '@planning/index'
import { useWrappedLocalPagination } from '@planning/stores'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { GeneralCargoOrderLocationItem } from '../GeneralCargo/Components/GeneralCargoOrderLocationItem'
import { OrderListUploadDialogV2 } from '../VesselVisit/Components/Upload/OrderListUploadDialog'
import { VisitGeneralCargoTable } from '../Visits/Components/organisms/VisitGeneralCargoTable'
import { RailVisitOrderListHeader } from './Components/Dashboard/RailVisitOrderListHeader'
import { DuplicatedRailcarAlerts } from './Components/Details/DuplicateRailcarsAlert'
import { AssignRailTracksDialog } from './Components/Dialogs/RailTrackAssignmentDialog'
import { RailVisitClosedWarning } from './Components/Molecules/RailVisitClosedWarning'
import { RailVisitViewToggleButtons } from './Components/Molecules/RailVisitViewToggleButton'
import { RailVisitTableOrderItem } from './Components/Table/RailVisitTableOrderItem'
import { RailVisitTableViewStore } from './Components/Table/RailVisitTableViewStore'
import {
  OperationStartedAlert,
  orderFilterDelegate,
  orderSortingDelegate,
  useContainerYardOperationStore,
} from './RailVisitDetailsPage'
import { RailcarPlanning } from './RailcarPlanning/RailcarPlanning'

interface Props {
  onOpenGeneralCargoReservation?: (order: OrderResponseDto) => void
  orderReservationsById?: Record<number, GeneralCargoOrderLocationItem[]>
}

export const RailVisitLoadPage = observer(
  ({ onOpenGeneralCargoReservation, orderReservationsById }: Props) => {
    const { id } = useParams()
    const railVisitId = Number(id)
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const {
      appViewStore,
      railVisitItemStore,
      railVisitQueryService,
      orderItemStore,
      containerItemStore,
      orderListUploadViewStoreV2,
    } = usePlanningStore()

    const [viewListMode, setViewListMode] = useState(true)
    const [isAssignTrackOpen, setIsAssignTrackOpen] = useState(false)
    const containerYardOperationViewStore = useContainerYardOperationStore()

    useAsyncFetch(async () => {
      if (!railVisitId) return
      await railVisitQueryService.fetchById(railVisitId)
      await orderItemStore.fetchByVisitId(railVisitId)
      await containerItemStore.fetchByVisitId(railVisitId)
    }, [railVisitId, railVisitQueryService, orderItemStore, containerItemStore])

    const railVisitItem = computed(() => railVisitItemStore.elements[railVisitId]).get()
    const isGeneralCargoVisit = railVisitItem?.data.cargoType === CargoType.GeneralCargo

    const tableStore = useMemo(
      () => new RailVisitTableViewStore(railVisitItem, 'Outbound'),
      [railVisitItem],
    )

    const loadOrdersListStore = useWrappedLocalPagination(
      () =>
        railVisitItem && isGeneralCargoVisit
          ? railVisitItem.load.generalCargoOrders
          : railVisitItem?.load.containerOrders ?? [],
      undefined,
      orderSortingDelegate,
      orderFilterDelegate,
    )

    const onUploadClick = async () => {
      orderListUploadViewStoreV2.reset()

      await orderListUploadViewStoreV2.setRailVisitId(railVisitId)
      await orderListUploadViewStoreV2.setDirection(CarrierVisitDirection.Outbound)

      orderListUploadViewStoreV2.setOpen(true)
    }

    const handleAssignRailTrack = async (
      selectedTrackIds: string[],
      targetTrackId?: string | null,
    ) => {
      try {
        appViewStore.showLoader()

        const currentVisit = railVisitItemStore.getById(railVisitId)

        if (!currentVisit) return

        await railVisitService.put({
          ...currentVisit.data,
          railTrackIds: selectedTrackIds.map(id => id),
          reassignmentTargetTrackId: targetTrackId ?? null,
        })

        await railVisitQueryService.fetchById(railVisitId)

        appViewStore.setShowAlert('success', t('tracksUpdated', 'Rail tracks updated successfully'))
      } catch (e) {
        console.error(e)
        appViewStore.setShowAlert('error', t('errorUpdatingTracks', 'Error updating tracks'))
      } finally {
        appViewStore.hideLoader()
      }
    }

    const toggleActionBtn = (
      <RailVisitViewToggleButtons
        currentView={viewListMode ? 'list' : 'plan'}
        onListClick={() => setViewListMode(true)}
        onPlanClick={() => setViewListMode(false)}
      />
    )

    return (
      <InitializationWrapper isInitializing={!railVisitItem}>
        <Box sx={{ backgroundColor: theme.palette.grey[200], minHeight: '100vh' }}>
          <RailVisitOrderListHeader
            railVisitItem={railVisitItem}
            direction='load'
            viewMode={viewListMode ? 'list' : 'plan'}
            onUploadClick={onUploadClick}
            onAssignRailTrack={() => setIsAssignTrackOpen(true)}
          />

          <Box>
            {!isGeneralCargoVisit &&
              (viewListMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', p: 3 }}>
                  <DuplicatedRailcarAlerts direction='Outbound' railVisitItem={railVisitItem} />

                  {railVisitItem?.data.isOutboundRailcarLengthConflicted && (
                    <Alert severity='warning' sx={{ mb: 2 }}>
                      {t(
                        'railcarLengthHigherThanTrackLength',
                        'Total length of railcars allocated...',
                      )}
                    </Alert>
                  )}

                  <RailVisitTableOrderItem
                    containerYardOperationViewStore={containerYardOperationViewStore!}
                    tableStore={tableStore}
                    renderTableCustomActions={() => (
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                        {toggleActionBtn}
                      </Box>
                    )}
                  />
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      p: '0px 16px',
                    }}
                  >
                    {railVisitItem?.hasOperationStarted && (
                      <Box flex={1}>
                        <OperationStartedAlert />
                      </Box>
                    )}
                    {railVisitItem?.data.status === CarrierVisitStatus.Closed && (
                      <Box flex={1}>
                        <RailVisitClosedWarning />
                      </Box>
                    )}
                    {toggleActionBtn}
                  </Box>
                  <Box sx={{ height: 'calc(100vh - 190px)' }}>
                    <RailcarPlanning
                      railVisitItem={railVisitItem}
                      direction={CarrierVisitDirection.Outbound}
                    />
                  </Box>
                </Box>
              ))}

            {isGeneralCargoVisit && (
              <Box padding={theme.customSpacing.m}>
                <VisitGeneralCargoTable
                  orders={[...loadOrdersListStore.pageItems]}
                  carrierVisit={railVisitItem}
                  carrierVisitType={CarrierType.Train}
                  direction={CarrierVisitDirection.Outbound}
                  onOpenGeneralCargoReservation={onOpenGeneralCargoReservation}
                  orderReservationsById={orderReservationsById}
                />
              </Box>
            )}
          </Box>

          <OrderListUploadDialogV2
            enableFieldSelectionFromOrdersView={false}
            open={orderListUploadViewStoreV2.open}
            handleDialogClose={() => {
              orderListUploadViewStoreV2.setOpen(false)
            }}
            visitType={CarrierType.Train}
          />

          <AssignRailTracksDialog
            open={isAssignTrackOpen}
            onClose={() => setIsAssignTrackOpen(false)}
            railVisitId={railVisitId}
            onSave={handleAssignRailTrack}
          />
        </Box>
      </InitializationWrapper>
    )
  },
)
